/**
 * المخططات — SVG بمسارات محسوبة (تخطيط بصري فقط، لا منطق مجال).
 * خط التنفيذ تركوازي 2.4px · خط العرض كهرماني 1.6px متقطع 5 5 · تعبئة 26%→0.
 */

import { color } from '@/design/tokens';
import type { PricePoint } from '@/domain/types';
import { num } from '@/lib/format';

function path(values: number[], w: number, h: number, min: number, max: number): string {
  if (values.length === 0) return '';
  const span = max - min || 1;
  return values
    .map((v, i) => {
      const x = (i / (values.length - 1 || 1)) * w;
      const y = h - ((v - min) / span) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

export function PriceChart({
  series,
  ariaLabel = 'مخطط سعر التنفيذ مقابل سعر العرض',
}: {
  series: PricePoint[];
  ariaLabel?: string;
}): JSX.Element {
  const W = 800;
  const H = 220;
  const deals = series.map((p) => p.deal);
  const asks = series.map((p) => p.ask);
  const min = Math.min(...deals, ...asks) * 0.96;
  const max = Math.max(...deals, ...asks) * 1.04;
  const dealPath = path(deals, W, H, min, max);
  const askPath = path(asks, W, H, min, max);
  const last = deals[deals.length - 1] ?? 0;
  const lastY = H - ((last - min) / (max - min || 1)) * H;

  return (
    <div data-testid="price-chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: 230, minHeight: 190, display: 'block' }}
        role="img"
        aria-label={ariaLabel}
      >
        <defs>
          <linearGradient id="nqFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color.teal} stopOpacity="0.26" />
            <stop offset="100%" stopColor={color.teal} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((g) => (
          <line
            key={g}
            x1="0"
            x2={W}
            y1={H * g}
            y2={H * g}
            stroke="rgba(255,255,255,.05)"
            strokeWidth="1"
          />
        ))}
        {dealPath ? <path d={`${dealPath} L${W},${H} L0,${H} Z`} fill="url(#nqFill)" /> : null}
        <path d={askPath} fill="none" stroke={color.amber} strokeWidth="1.6" strokeDasharray="5 5" />
        <path d={dealPath} fill="none" stroke={color.teal} strokeWidth="2.4" />
        <circle cx={W} cy={lastY} r="3.6" fill={color.teal} />
      </svg>
      <div
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          marginTop: 10,
          fontSize: 11.5,
          color: color.label,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 14, height: 2, background: color.teal }} aria-hidden="true" />
          سعر التنفيذ
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
          <span
            style={{
              width: 14,
              height: 0,
              borderTop: `2px dashed ${color.amber}`,
            }}
            aria-hidden="true"
          />
          سعر العرض
        </span>
        <span className="nq-num" dir="ltr" style={{ marginInlineStart: 'auto' }}>
          {num(last)} ر.س/م²
        </span>
      </div>
    </div>
  );
}

export function Sparkline({ values }: { values: number[] }): JSX.Element {
  const W = 120;
  const H = 40;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const d = path(values, W, H, min * 0.99, max * 1.01);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="اتجاه السعر">
      <path d={d} fill="none" stroke={color.teal} strokeWidth="1.8" />
    </svg>
  );
}
